import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Sidebar from 'react-sidebar';
import { isAuthenticated } from '../../services/Auth/AuthService';
import { getElementID } from '../../utils/HTMLUtils';
import {
  agendaAssessoresPedagogicosRoute,
  contratosRoute,
  cooperativasEscolares,
  dashboardRoute,
  financasNaMochila,
  projetosRoute,
  relatoriosRoute,
  uniaoFazAVidaRoute,
} from './../../library/Routes';
import styles from './LeftSidebar.scss';
import { toClose, toOpen } from './LeftSidebarActions';
import LeftSidebarMenuItem from './LeftSidebarMenuItem';
import { PROFILE_PUFV_OPERACIONAL } from '../../library/Profiles';
import CloseIconModal from 'assets/icons/CloseIconModal';
import { LogoProgramasEducacionais } from 'components/LogoProgramasEducacionais/LogoProgramasEducacionais';
import ResumeIcon from 'components/Icons/ResumeIcon';
import PeopleIcon from 'components/Icons/PeopleIcon';
import ChartIcon from 'components/Icons/ChartIcon';
import DownloadIcon from 'components/Icons/DownloadIcon';
import PictureUserIcon from 'components/Icons/PictureUserIcon';
import CircleChartIcon from 'components/Icons/CircleChartIcon';
import FileIcon from 'components/Icons/FileIcon';

const rootClassName = styles.rootClassName;
const rootElementId = 'left-sidebar';

const LeftSidebar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const menuItemsRef = useRef(null);

  const languageValues = useSelector(state => state.languages.languageValues);
  const opened = useSelector(state => state.leftSidebar.opened);
  const auth = useSelector(state => state.auth);

  const getMenuItems = useCallback(() => {
    let arr = [];
    if (auth.userPermissions.projetos.list) {
      arr.push({
        normalIcon: <ResumeIcon />,
        hoverIcon: <ResumeIcon color='#ffffff' />,
        label: languageValues.leftSideBar.projetos,
        path: projetosRoute.path,
        id: 'projetos',
      });
    }
    if (auth.userPermissions.projetos.list) {
      arr.push({
        normalIcon: <ResumeIcon />,
        hoverIcon: <ResumeIcon color='#ffffff' />,
        label: languageValues.leftSideBar.registro_pedagogico,
        path: `${window.env.auth.PE_WEB_URL.registro_pedagogico}`,
        id: 'registro',
      });
    }
    if (auth.userPermissions.professores.list) {
      arr.push({
        normalIcon: <PeopleIcon />,
        hoverIcon: <PeopleIcon color='#ffffff' />,
        label: languageValues.leftSideBar.gestao,
        path: `${window.env.auth.PE_WEB_URL.gestao_cadastro}`,
        id: 'gestao',
      });
    }
    if (auth.userPermissions.contratos.list) {
      arr.push({
        normalIcon: <FileIcon />,
        hoverIcon: <FileIcon color='#ffffff' />,
        label: languageValues.leftSideBar.contratos,
        path: `${window.env.auth.PE_WEB_URL.contratos}`,
        id: 'contratos',
      });
    }
    if (
      auth.userPermissions.relatorios.generate.projetos ||
      auth.userPermissions.relatorios.generate['dados-cadastrais'] ||
      auth.userPermissions.relatorios.generate['dados-gerais'] ||
      auth.userPermissions.relatorios.generate['dados-do-programa'] ||
      auth.userPermissions.relatorios.generate['assessoria'] ||
      auth.userPermissions.relatorios.generate['contratos']
    ) {
      arr.push({
        normalIcon: <ChartIcon />,
        hoverIcon: <ChartIcon color='#ffffff' />,
        label: languageValues.leftSideBar.relatorios,
        path: relatoriosRoute.path,
        id: 'relatorios',
      });
    }
    if (auth.userPermissions.materiaisPedagogicos.list) {
      arr.push({
        normalIcon: <DownloadIcon />,
        hoverIcon: <DownloadIcon color='#ffffff' />,
        label: languageValues.leftSideBar.materiais,
        path: '',
        id: 'materiais-pedagogicos',
        subMenus: [
          {
            idSubMenu: 'uniao-faz-a-vida',
            labelSubMenu: languageValues.leftSideBar.pufv,
            pathSubMenu: uniaoFazAVidaRoute.path,
          },
          {
            idSubMenu: 'cooperativas-escolares',
            labelSubMenu: languageValues.leftSideBar.cooperativasEscolares,
            pathSubMenu: cooperativasEscolares.path,
          },
          {
            idSubMenu: 'financas-na-mochila',
            labelSubMenu: languageValues.leftSideBar.financasNaMochila,
            pathSubMenu: financasNaMochila.path,
          },
        ],
      });
    }
    if (auth.userPermissions.agendaAssessoresPedagogicos.list) {
      arr.push({
        normalIcon: <PictureUserIcon />,
        hoverIcon: <PictureUserIcon color='#ffffff' />,
        label: languageValues.leftSideBar.agendaAssessoresPedagogicos,
        path: agendaAssessoresPedagogicosRoute.path,
        id: 'agenda-assessores-pedagogicos',
      });
    }
    if (auth.userPermissions.dashboard.generate) {
      arr.push({
        normalIcon: <CircleChartIcon />,
        hoverIcon: <CircleChartIcon color='#ffffff' />,
        label: languageValues.leftSideBar.dashboard,
        path: dashboardRoute.path,
        id: 'dashboard',
      });
    }
    return arr;
  }, [auth, languageValues]);

  const toHomePage = () => {
    auth.userData.perfil === PROFILE_PUFV_OPERACIONAL.id
      ? history.push(contratosRoute.path)
      : history.push(projetosRoute.path);
  };

  const sidebarContent = () => (
    <div className={rootClassName + '-content'}>
      <div className={rootClassName + '-header'}>
        <button
          id={getElementID('left-sidebar', 'logo')}
          className={rootClassName + '-logotipo'}
          onClick={toHomePage}
        >
          <LogoProgramasEducacionais />
        </button>
        <button
          id={getElementID('left-sidebar', 'close')}
          className={rootClassName + '-close'}
          onClick={() => dispatch(toClose())}
        >
          <CloseIconModal style={{ height: 20, width: 20 }} />
        </button>
      </div>
      <div className={rootClassName + '-menu-items'} ref={menuItemsRef}>
        {getMenuItems().map((item, index) => (
          <LeftSidebarMenuItem
            path={item.path}
            id={getElementID(rootElementId, item.id)}
            key={index}
            normalIcon={item.normalIcon}
            hoverIcon={item.hoverIcon}
            label={item.label}
            subMenus={item.subMenus ?? []}
          />
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    if (!opened) {
      setTimeout(() => {
        if (menuItemsRef.current) {
          menuItemsRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [opened]);

  const onSetSidebarOpen = (open) => {
    if (open) {
      dispatch(toOpen());
    } else {
      dispatch(toClose());
    }
  };

  const sidebarProps = {
    sidebar: sidebarContent(),
    open: opened,
    onSetOpen: onSetSidebarOpen,
    sidebarClassName: rootClassName,
    overlayClassName: rootClassName + '-overlay',
  };

  return isAuthenticated() ? (
    <Sidebar {...sidebarProps}>
      <></>
    </Sidebar>
  ) : (
    ''
  );
};

export default LeftSidebar;